import type {
  ElUploadInternalFileDetail,
  ElUploadInternalRawFile,
} from 'element-ui/types/upload'
import {
  getFileExtension,
  getFileName,
  isMp3,
  isMp4,
  isPng,
  isZip,
  removeFileExtension,
} from '@com/utils/file'
import shortid from 'shortid'
import {
  ResourceDetectResult,
  XlzResourceDetectResult,
} from '@ps/types/store/uploader.type'
import { ValidatorResult } from '@com/utils/file-validator'
import { validate as audioValidate } from './audio'
import { isDragonBone, validate as dragonBoneValidate } from './dragon-bone'
import { validate as imageValidate } from './image'
import { validate as videoValidate } from './video'
import { validate as xlzValidate } from './xlz'
import JSZip from 'jszip'
import mime from 'mime'
import { isXlzZip } from '@com/utils/xlz-file'
import i18n from '@ps/lang'
import { sortFileList } from '@ps/workbench/resource-manager/uploader/xlz'
import { checkPermission } from '@ps/views/workbench/resource-manager/resource'
import store from '@ps/store'
import { SceneType } from '@ps/bridge/qc/scripts/scene-config'

/**
 * isImage 判断是否是 png、jpg、jpeg
 * @param {string} url 文件名或者文件URL，test.png
 */
export const imageTypes = ['png', 'jpg', 'jpeg']
export const isImage = (url: string): boolean => {
  // return /(\.jpeg|\.jpg|\.png)$/i.test(url)
  return imageTypes.includes(getFileExtension(url))
}

/**
 * https://confluence.mobvista.com/pages/viewpage.action?pageId=58830795
 * @param name
 */
const isXlz = (path: string): boolean => {
  const fileName = getFileName(path)

  const name = removeFileExtension(fileName)
  return /^([^\s+])*\d+$/.test(name) && isPng(fileName)
}

const pickXlz = async (
  fileList: ElUploadInternalFileDetail[],
  rawFile?: File,
) => {
  const xlzFileList = fileList
    .filter((file: ElUploadInternalFileDetail) => {
      return isXlz(file.name)
    })
    .map((file) => {
      const name = removeFileExtension(file.name)
      const prefix = name.replace(/\d+$/, '')

      return {
        file,
        resourceType: 'xlz',
        prefix: prefix, // 前缀
        uuid: shortid(),
      }
    })

  const xlzMap: {
    [key: string]: XlzResourceDetectResult
  } = {}
  xlzFileList.forEach((file) => {
    if (!xlzMap[file.prefix]) {
      xlzMap[file.prefix] = {
        validResult: {
          valid: true,
          errors: [],
        },
        uuid: shortid(),
        fileList: [],
        prefix: '',
        resourceType: 'xlz',
        progress: 0,
        name: rawFile?.name,
        raw: rawFile,
      }
    }
    xlzMap[file.prefix].fileList.push(file)
  })

  let imageFileList: ElUploadInternalFileDetail[] = []

  for (const key in xlzMap) {
    const xlzGroup = xlzMap[key]

    // 如果资源命名符合序列帧规范，但是文件个数不超过 2，则认为不是序列帧
    if (xlzGroup.fileList.length <= 1) {
      imageFileList = [
        ...imageFileList,
        ...xlzMap[key].fileList.map((item) => item.file),
      ]
      delete xlzMap[key]
    }

    // 验证 rawFile大小是否超过
    const { valid, errors } = await xlzValidate(
      xlzGroup.fileList.map((item) => item.file),
      rawFile,
    )
    xlzGroup.validResult = {
      valid,
      errors,
    }
    xlzGroup.prefix = xlzGroup.fileList[0].prefix
  }
  return { xlzMap, imageFileList }
}

const detectZipFile = async (
  file: ElUploadInternalFileDetail,
): Promise<ResourceDetectResult> => {
  const validResult = (await dragonBoneValidate(file)) as ValidatorResult
  const { valid } = validResult
  return {
    file,
    resourceType: 'dragonBone',
    uuid: shortid(),
    validResult: {
      valid,
      errors: [validResult],
    },
    progress: 0,
  }
}

const pickImage = (fileList: ElUploadInternalFileDetail[]) => {
  return fileList
    .filter((file: ElUploadInternalFileDetail) => {
      return isImage(file.name) /* && !isXlz(file.name) */
    })
    .map((file) => ({
      file,
      resourceType: 'image',
      uuid: shortid(),
      validResult: imageValidate(file.raw),
      progress: 0,
    }))
}

const pickZip = (
  fileList: ElUploadInternalFileDetail[],
): Promise<ResourceDetectResult[]> => {
  const zipFiles = fileList.filter((file: ElUploadInternalFileDetail) => {
    return isZip(file.name)
  })
  return Promise.all(zipFiles.map((file) => detectZipFile(file)))
}

const pickVideo = async (fileList: ElUploadInternalFileDetail[]) => {
  return fileList
    .filter((file) => isMp4(file.name))
    .map((file) => ({
      file,
      resourceType: 'video',
      uuid: shortid(),
      validResult: videoValidate(file.raw),
      progress: 0,
    }))
}

const pickZipXlz = async (fileList: ElUploadInternalFileDetail[]) => {
  const xlzs: XlzResourceDetectResult[] = []
  const raws: ElUploadInternalFileDetail[] = []
  for (const file of fileList) {
    if (!isZip(file.name)) {
      raws.push(file)
      // eslint-disable-next-line no-continue
      continue
    }

    // 一定是序列帧
    try {
      const result = await unZipXlz(file.raw)
      xlzs.push(...result)
    } catch (error) {
      raws.push(file)
      console.log(error)
    }
  }

  return { xlzs, raws }
}

const pickDragonBone = async (fileList: ElUploadInternalFileDetail[]) => {
  const files: ResourceDetectResult[] = []
  const raws: ElUploadInternalFileDetail[] = []
  for (const file of fileList) {
    if (!isZip(file.name)) {
      raws.push(file)
      // eslint-disable-next-line no-continue
      continue
    }

    try {
      const result = await isDragonBone(file)
      if (result) {
        const result = await detectZipFile(file)
        files.push(result)
      } else {
        raws.push(file)
      }
    } catch (error) {
      raws.push(file)
      console.log(error)
    }
  }

  return { files, raws }
}

export async function unZipXlz(file: File): Promise<XlzResourceDetectResult[]> {
  const _isxlz = await isXlzZip(file)
  if (!_isxlz) {
    return Promise.reject(new Error('不是xlz zip文件'))
  }

  const zip = await JSZip.loadAsync(file)

  const files: Promise<File>[] = zip
    .filter(
      (fileName, file) =>
        !file.dir &&
        !/^(__MACOSX)|(\.DS_Store)/.test(file.name) &&
        isXlz(fileName),
    )
    .map(function (zipFile) {
      // 获取文件内容并转换为Blob对象
      return zipFile.async('blob').then(async function (blob) {
        // 将Blob对象转换为File对象

        return new File([blob], getFileName(zipFile.name), {
          type: mime.getType(zipFile.name) ?? 'image/png',
        })
      })
    })

  return Promise.all(files).then(async (files) => {
    console.log('files', files)

    if (files.length === 0) {
      return Promise.reject(new Error('未找到 xlz 文件'))
    }

    const xlzFiles: ElUploadInternalFileDetail[] = files.map((file) =>
      createElUploadFile(file),
    )
    const { xlzMap } = await pickXlz(sortFileList(xlzFiles), file)

    return Object.values(xlzMap)
  })
}
let tempIndex = 0
export function createElUploadFile(file: File): ElUploadInternalFileDetail {
  const uid = Date.now() + tempIndex++

  const rawFile = file as ElUploadInternalRawFile
  rawFile.uid = uid
  return {
    status: 'ready',
    name: rawFile.name,
    size: rawFile.size,
    percentage: 0,
    uid: uid,
    raw: rawFile,
  }
}

const checkResourcePermission = (data: any) => {
  type CategoryMap = {
    [key: string]: string
  }
  const categoryMap: CategoryMap = {
    xlz: 'effect',
    image: 'image',
    audio: 'audio',
    video: 'video',
    zip: 'effect',
  }

  // 玩法模板禁止资源
  const playUnitForbiddenResource = ['audio']
  const sceneEditType =
    store.state.workspace.game.sceneManager.sceneEditType || ''
  for (const key in data) {
    // 不支持对应的资源类型
    if (categoryMap[key] && !checkPermission(categoryMap[key])) {
      data[key].forEach((item: any) => {
        let msg = i18n.t('ps.resourcePool.resource.uploadForbidden.type')
        item.validResult.valid = false
        if (
          sceneEditType === SceneType.PLAY &&
          playUnitForbiddenResource.indexOf(key) !== -1
        ) {
          msg = i18n.t('ps.resourcePool.resource.uploadForbidden.play')
        }
        if (item.validResult.errors && item.validResult.errors.length > 0) {
          item.validResult.errors.map((error: any) => {
            return {
              ...error,
              message: msg,
            }
          })
        } else {
          item.validResult.errors = [
            {
              message: msg,
            },
          ]
        }
      })
    }
  }
}

export async function detectFileType(
  _fileList: ElUploadInternalFileDetail[],
): Promise<{
  xlz: XlzResourceDetectResult[]
  image: ResourceDetectResult[]
  audio: ResourceDetectResult[]
  video: ResourceDetectResult[]
  zip: ResourceDetectResult[]
  others: ResourceDetectResult[]
}> {
  // 文件列表优先处理xlz，序列帧优先级高于zip，因为zip可能包含序列帧，
  const { xlzs, raws: xlzOtherfileList } = await pickZipXlz(_fileList)

  const { files: dragonBoneFiles, raws: dragonboneOtherfileList } =
    await pickDragonBone(xlzOtherfileList)

  const fileList = dragonboneOtherfileList
  const videoFiles = await pickVideo(fileList)
  const otherFiles = fileList
    .filter(
      (file) => !isImage(file.name) && !isMp3(file.name) && !isMp4(file.name),
    )
    .map((file) => ({
      file,
      resourceType: getFileExtension(file.name),
      uuid: shortid(),
      validResult: {
        valid: false,
        errors: [
          {
            valid: false,
            message: i18n.t('ps.upload.unsupportedFileTip'),
            type: 'file type',
          },
        ],
      },
      progress: 0,
    }))

  const temp = {
    xlz: xlzs,
    image: [...pickImage(fileList)],
    audio: fileList
      .filter((file) => isMp3(file.name))
      .map((file) => ({
        file,
        resourceType: 'audio',
        uuid: shortid(),
        validResult: audioValidate(file.raw),
        progress: 0,
      })),
    video: videoFiles,
    // 龙骨文件
    zip: dragonBoneFiles,
    others: otherFiles,
  }
  checkResourcePermission(temp)
  return temp
}
